import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Divider,
  Grid,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { useQuery } from "@tanstack/react-query";
import HttpService from "../../../services/HttpService";
import CardStatsElement from "../../../components/CardStatsElement";
import Movements from "../../../components/Movements";
import Labels from "../../../labels/Labels";

export default function SubDepartmentRow({
  department,
  isSelected,
  showType,
  onShowStats,
  onShowMovements,
}) {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    idEmployer,
    employerDescription,
    currentBalance,
    creditsThisMonth,
    debitsThisMonth,
  } = department;

  // Fetch stats when expanded and showType is "stats"
  async function fetchBalanceStats() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/balance/stats?idEmployer=` + idEmployer
    );
    return response;
  }

  const {
    data: dataSingleStats,
    isLoading: isLoadingSingleStats,
    isError: isErrorSingleStats,
  } = useQuery(["balanceStats", idEmployer], fetchBalanceStats, {
    enabled: isSelected && showType === "stats",
  });

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      // Default to showing stats when expanding
      onShowStats(idEmployer);
    }
  };

  const handleShowStats = () => {
    setIsExpanded(true);
    onShowStats(idEmployer);
  };

  const handleShowMovements = () => {
    setIsExpanded(true);
    onShowMovements(idEmployer);
  };

  const hasActivity = creditsThisMonth > 0 || debitsThisMonth > 0;

  return (
    <Box
      sx={{
        border: isSelected
          ? "1px solid " + theme.palette.primary.main
          : "1px solid #e0e0e0",
        borderRadius: 1,
        marginBottom: 1,
      }}
    >
      {/* Row Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: 1.5,
          cursor: "pointer",
          "&:hover": { backgroundColor: "#fafafa" },
        }}
        onClick={handleToggle}
      >
        {/* Expand Icon */}
        <IconButton size="small" sx={{ marginRight: 1 }}>
          {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </IconButton>

        {/* Department Name */}
        <Typography
          variant="body2"
          sx={{ flex: 1, fontWeight: isSelected ? "bold" : "normal" }}
        >
          {employerDescription} - [{idEmployer}]
        </Typography>

        {/* Balance */}
        <Typography
          variant="body2"
          sx={{
            width: "120px",
            textAlign: "right",
            fontWeight: currentBalance > 0 ? "bold" : "normal",
            color: currentBalance > 0 ? "text.primary" : "text.secondary",
          }}
        >
          ${" "}
          {Number(currentBalance).toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })}
        </Typography>

        {/* Credits (Month) */}
        <Typography
          variant="body2"
          sx={{
            width: "120px",
            textAlign: "right",
            color: creditsThisMonth > 0 ? "text.primary" : "text.secondary",
          }}
        >
          ${" "}
          {Number(creditsThisMonth).toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })}
        </Typography>

        {/* Debits (Month) */}
        <Typography
          variant="body2"
          sx={{
            width: "120px",
            textAlign: "right",
            color: debitsThisMonth > 0 ? "#d32f2f" : "#999",
          }}
        >
          ${" "}
          {Number(debitsThisMonth).toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })}
        </Typography>

        {/* Action Icons */}
        <Box
          sx={{
            width: "80px",
            display: "flex",
            justifyContent: "flex-end",
            gap: 0.5,
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleShowStats();
            }}
            sx={{ color: theme.palette.primary.main }}
          >
            <AssessmentIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleShowMovements();
            }}
            sx={{ color: theme.palette.primary.main }}
          >
            <ListAltIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Expanded Content */}
      <Collapse in={isExpanded && isSelected} unmountOnExit>
        <Divider />
        <Box sx={{ padding: 2, backgroundColor: "#fafafa" }}>
          {showType === "stats" && (
            <Grid container spacing={1}>
              <CardStatsElement
                key={`cTM-${idEmployer}`}
                headerTypograhy="h8"
                minWidth={10}
                fontSize={20}
                md={4}
                isErrorStats={isErrorSingleStats}
                isLoadingStats={isLoadingSingleStats}
                text={Labels.getLabel(
                  "queryBalance.CreditsThisMonth",
                  "Credits this month"
                )}
                data={dataSingleStats && dataSingleStats.data.creditsThisMonth}
                type="amount"
              />
              <CardStatsElement
                key={`cTW-${idEmployer}`}
                headerTypograhy="h8"
                minWidth={10}
                fontSize={20}
                md={4}
                isErrorStats={isErrorSingleStats}
                isLoadingStats={isLoadingSingleStats}
                text={Labels.getLabel(
                  "queryBalance.CreditsThisWeek",
                  "Credits this week"
                )}
                data={dataSingleStats && dataSingleStats.data.creditsThisWeek}
                type="amount"
              />
              <CardStatsElement
                key={`cT-${idEmployer}`}
                headerTypograhy="h8"
                minWidth={10}
                fontSize={20}
                md={4}
                isErrorStats={isErrorSingleStats}
                isLoadingStats={isLoadingSingleStats}
                text={Labels.getLabel(
                  "queryBalance.CreditsToday",
                  "Credits today"
                )}
                data={dataSingleStats && dataSingleStats.data.creditsToday}
                type="amount"
              />
              <CardStatsElement
                key={`dTM-${idEmployer}`}
                headerTypograhy="h8"
                minWidth={10}
                fontSize={20}
                md={4}
                isErrorStats={isErrorSingleStats}
                isLoadingStats={isLoadingSingleStats}
                text={Labels.getLabel(
                  "queryBalance.DebitsThisMonth",
                  "Debits this month"
                )}
                data={dataSingleStats && dataSingleStats.data.debitsThisMonth}
                type="amount"
              />
              <CardStatsElement
                key={`dTW-${idEmployer}`}
                headerTypograhy="h8"
                minWidth={10}
                fontSize={20}
                md={4}
                isErrorStats={isErrorSingleStats}
                isLoadingStats={isLoadingSingleStats}
                text={Labels.getLabel(
                  "queryBalance.DebitsThisWeek",
                  "Debits this week"
                )}
                data={dataSingleStats && dataSingleStats.data.debitsThisWeek}
                type="amount"
              />
              <CardStatsElement
                key={`dT-${idEmployer}`}
                headerTypograhy="h8"
                minWidth={10}
                fontSize={20}
                md={4}
                isErrorStats={isErrorSingleStats}
                isLoadingStats={isLoadingSingleStats}
                text={Labels.getLabel(
                  "queryBalance.DebitsToday",
                  "Debits today"
                )}
                data={dataSingleStats && dataSingleStats.data.debitsToday}
                type="amount"
              />
            </Grid>
          )}
          {showType === "movements" && (
            <Movements
              title={Labels.getLabel("queryBalance.Movements", "Movements")}
              key={idEmployer}
              idEmployer={idEmployer}
              idBalance={idEmployer}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
